import { createContext, useCallback, useContext } from 'react';
import { OverridedMixpanel } from 'mixpanel-browser';
import { logger } from 'utils/logger';
import { AdditionalDetails, MixpanelEvent } from './MixPanelTypes';
import { isEnriched, useEnrichEvent } from './useEnrichEvent';
import { useTenantlessRoute } from 'src/utils/routing/useTenantlessRoute';

const context = createContext<OverridedMixpanel | null>(null);

export default context;

export const { Consumer, Provider } = context;

export type UserTracker = <Type = AdditionalDetails>(event: MixpanelEvent, additionalDetail?: Type) => void;

/*
 * Ignore Mixpanel calls for test and local dev environments. This is to
 * prevent us from exceeding our plan threshold of 10,000 MTU (Monthly Tracked Users).
 *
 * LOCAL DEBUGGING: To enable debugging for Mixpanel on local development environments:
 *
 * 1. Edit your .env.serve.local and set the following variables:
 *
 * DISABLE_MIXPANEL=false
 * MIXPANEL_ACCESS_TOKEN=<copy token from packages/enterprise/.env>
 *
 * 2. You should comment out these changes when finished debugging.
 *
 * */
export const shouldIgnoreMixpanel = process.env.NODE_ENV === 'test' || process.env.DISABLE_MIXPANEL === 'true';

export const useMixpanel = () => {
  const { enrichEvent } = useEnrichEvent();
  const mixpanel = useContext(context);
  const { route } = useTenantlessRoute();
  const track: UserTracker = useCallback(
    <Type,>(event: MixpanelEvent, additionalDetail?: Type) => {
      try {
        const richEvent: MixpanelEvent = enrichEvent(event);
        if (!shouldIgnoreMixpanel && isEnriched(richEvent)) {
          mixpanel?.track(event.eventKey, { ...richEvent, ...additionalDetail });
        }
      } catch (e) {
        logger.log('Mixpanel was not initialized correctly:', e);
      }
    },
    [enrichEvent, mixpanel]
  );
  return { mixpanel, route, track };
};
